/**
 * Smart Landlord AI Assistant Hook
 *
 * Context-aware assistant that sends user data to Gemini AI
 * for personalized, actionable responses.
 *
 * Features:
 * - Time-of-day personalized welcome
 * - Guided setup mode for new users (can be dismissed)
 * - Form-context aware suggestions
 * - Proactive nudges after user actions
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { getAuthToken } from '@/lib/supabase';
import { reportAIError } from '@/lib/errorMonitoring';
import type {
  SmartAssistantMessage,
  SmartAssistantResponse,
  AssistantAction,
  AssistantMode,
  SmartAssistantOptions,
} from '@/types/assistant';
import { useCreateTicket, captureBrowserContext } from '@/hooks/useSupport';
import { useOnboardingStore } from '@/stores/onboardingStore';

// ─── Time of Day Helper ───

function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' | 'night' {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 23) return 'evening';
  return 'night';
}

function getGreeting(name: string, language: 'hr' | 'en'): string {
  const period = getTimeOfDay();
  const n = name ? ` ${name}` : '';

  if (language === 'hr') {
    switch (period) {
      case 'morning': return `Dobro jutro${n}! ☀️`;
      case 'afternoon': return `Bok${n}! 👋`;
      case 'evening': return `Dobar večer${n}! 🌙`;
      case 'night': return `Hej${n}! 😄`;
    }
  } else {
    switch (period) {
      case 'morning': return `Good morning${n}! ☀️`;
      case 'afternoon': return `Hi${n}! 👋`;
      case 'evening': return `Good evening${n}! 🌙`;
      case 'night': return `Hey${n}! 😄`;
    }
  }
}

// ─── Guided Setup (delegated to onboarding store) ───
// Guided/dismissed state lives in src/stores/onboardingStore.ts as the single
// source of truth. The legacy GUIDED_SETUP_KEY localStorage flag was removed
// — Zustand's persist middleware handles persistence under 'bookiapp-onboarding'.

// ─── Quick Suggestions ───

type SugItem = { icon: string; label: string; query: string };
type SugLang = { hr: SugItem[]; en: SugItem[] };

const FORM_SUGGESTIONS: Record<string, SugLang> = {
  'property': {
    hr: [
      { icon: '✍️', label: 'Generiraj opis', query: 'Napiši opis za moj objekt' },
      { icon: '📶', label: 'Postavi WiFi', query: 'Kako postaviti WiFi za sve apartmane?' },
      { icon: '💡', label: 'Savjeti za tip', query: 'Koji tip objekta da odaberem?' },
    ],
    en: [
      { icon: '✍️', label: 'Generate description', query: 'Write a description for my property' },
      { icon: '📶', label: 'Set up WiFi', query: 'How to set WiFi for all units?' },
      { icon: '💡', label: 'Type tips', query: 'Which property type should I choose?' },
    ],
  },
  'unit': {
    hr: [
      { icon: '✍️', label: 'Generiraj opis', query: 'Napiši opis za ovaj apartman' },
      { icon: '📋', label: 'Napiši pravila', query: 'Generiraj kućna pravila za apartman' },
      { icon: '🏠', label: 'Check-in upute', query: 'Napiši upute za self check-in' },
      { icon: '❓', label: 'Što nedostaje?', query: 'Što još trebam popuniti za ovaj apartman?' },
    ],
    en: [
      { icon: '✍️', label: 'Generate description', query: 'Write a description for this unit' },
      { icon: '📋', label: 'Write rules', query: 'Generate house rules for this unit' },
      { icon: '🏠', label: 'Check-in instructions', query: 'Write self check-in instructions' },
      { icon: '❓', label: "What's missing?", query: "What else do I need to fill for this unit?" },
    ],
  },
  'booking': {
    hr: [
      { icon: '📅', label: 'Kako radi?', query: 'Kako funkcionira rezervacija i app za goste?' },
      { icon: '💰', label: 'Cijene', query: 'Kako se izračunava cijena?' },
      { icon: '🔗', label: 'Guest link', query: 'Kako podijeliti link gostu?' },
    ],
    en: [
      { icon: '📅', label: 'How it works?', query: 'How does booking and Guest App work?' },
      { icon: '💰', label: 'Pricing', query: 'How is the price calculated?' },
      { icon: '🔗', label: 'Guest link', query: 'How to share the link with a guest?' },
    ],
  },
  // Smart Setup wizard — generic chips; the in-wizard coach also injects a
  // step-specific "Pomozi mi s ovim korakom" question with the live step id.
  'express_wizard': {
    hr: [
      { icon: '🤔', label: 'Trebam li ovo?', query: 'Moram li ovaj korak ispuniti ili ga mogu preskočiti?' },
      { icon: '📱', label: 'Što gost može?', query: 'Što gost može sam u aplikaciji da ne moram sve pisati?' },
      { icon: '✍️', label: 'Napiši tekst', query: 'Možeš li mi napisati tekst za ovaj korak?' },
    ],
    en: [
      { icon: '🤔', label: 'Do I need this?', query: 'Do I have to fill this step or can I skip it?' },
      { icon: '📱', label: 'What can the guest do?', query: 'What can the guest do in the app so I don\'t have to write everything?' },
      { icon: '✍️', label: 'Write the text', query: 'Can you write the text for this step?' },
    ],
  },
};

// Page-aware suggestions based on current URL path (no FormContext needed)
const PAGE_SUGGESTIONS: Record<string, SugLang> = {
  '/dashboard/calendar': {
    hr: [
      { icon: '📆', label: 'iCal sync', query: 'Kako sinkronizirati kalendar s Booking.com i Airbnb?' },
      { icon: '💰', label: 'Sezonske cijene', query: 'Kako postaviti sezonske cijene?' },
      { icon: '🚫', label: 'Blokiraj datume', query: 'Kako blokirati datume?' },
      { icon: '🔍', label: 'Dostupnost', query: 'Provjeri dostupnost mojih apartmana' },
    ],
    en: [
      { icon: '📆', label: 'iCal sync', query: 'How to sync calendar with Booking.com and Airbnb?' },
      { icon: '💰', label: 'Seasonal prices', query: 'How to set seasonal prices?' },
      { icon: '🚫', label: 'Block dates', query: 'How to block dates?' },
      { icon: '🔍', label: 'Availability', query: 'Check availability of my units' },
    ],
  },
  '/dashboard/messages': {
    hr: [
      { icon: '🌐', label: 'Auto prijevod', query: 'Kako radi automatski prijevod poruka?' },
      { icon: '🔔', label: 'Push obavijesti', query: 'Kako uključiti push notifikacije za poruke?' },
      { icon: '💬', label: 'Chat značajke', query: 'Koje sve značajke ima chat?' },
    ],
    en: [
      { icon: '🌐', label: 'Auto translate', query: 'How does automatic message translation work?' },
      { icon: '🔔', label: 'Push notifications', query: 'How to enable push notifications for messages?' },
      { icon: '💬', label: 'Chat features', query: 'What features does the chat have?' },
    ],
  },
  '/dashboard/smart-inbox': {
    hr: [
      { icon: '📧', label: 'Što je Smart Inbox?', query: 'Što je Smart Inbox i kako mi pomaže?' },
      { icon: '🔗', label: 'Spoji Gmail', query: 'Kako spojiti Gmail sa Smart Inboxom?' },
      { icon: '⚙️', label: 'Pravila odgovora', query: 'Kako postaviti pravila za AI odgovore?' },
      { icon: '🧠', label: 'Nauči moj stil', query: 'Kako AI može naučiti moj stil iz Gmaila?' },
      { icon: '💰', label: 'Gost je uplatio', query: 'Kako poslati potvrdu rezervacije kad gost uplati?' },
      { icon: '💸', label: 'Koliko košta?', query: 'Koliko košta Smart Inbox i AI analiza?' },
    ],
    en: [
      { icon: '📧', label: 'What is Smart Inbox?', query: 'What is Smart Inbox and how does it help me?' },
      { icon: '🔗', label: 'Connect Gmail', query: 'How to connect Gmail to Smart Inbox?' },
      { icon: '⚙️', label: 'Reply rules', query: 'How to set up rules for AI replies?' },
      { icon: '🧠', label: 'Learn my style', query: 'How can AI learn my style from Gmail?' },
      { icon: '💰', label: 'Guest paid', query: 'How to send booking confirmation when guest pays?' },
      { icon: '💸', label: 'How much it costs?', query: 'How much does Smart Inbox and AI analysis cost?' },
    ],
  },
  '/dashboard/local-guide': {
    hr: [
      { icon: '🍽️', label: 'Dodaj preporuku', query: 'Kako dodati preporuku za restoran ili plažu?' },
      { icon: '🛠️', label: 'Moje usluge', query: 'Kako dodati vlastite usluge za goste?' },
      { icon: '🚌', label: 'Transport', query: 'Kako radi AI transport search?' },
    ],
    en: [
      { icon: '🍽️', label: 'Add recommendation', query: 'How to add a restaurant or beach recommendation?' },
      { icon: '🛠️', label: 'My services', query: 'How to add my own services for guests?' },
      { icon: '🚌', label: 'Transport', query: 'How does AI transport search work?' },
    ],
  },
  '/dashboard/payment-methods': {
    hr: [
      { icon: '💳', label: 'Načini plaćanja', query: 'Koje načine plaćanja mogu ponuditi gostima?' },
      { icon: '🏦', label: 'IBAN postavke', query: 'Kako postaviti bankovni prijenos s IBAN-om?' },
      { icon: '💰', label: 'Praćenje uplata', query: 'Kako pratiti uplate gostiju?' },
    ],
    en: [
      { icon: '💳', label: 'Payment methods', query: 'What payment methods can I offer to guests?' },
      { icon: '🏦', label: 'Bank transfer', query: 'How to set up bank transfer with IBAN?' },
      { icon: '💰', label: 'Track payments', query: 'How to track guest payments?' },
    ],
  },
  '/dashboard/team': {
    hr: [
      { icon: '👥', label: 'Dodaj člana', query: 'Kako dodati člana tima?' },
      { icon: '📞', label: 'Kontakt osoba', query: 'Kako postaviti glavnu kontakt osobu za goste?' },
    ],
    en: [
      { icon: '👥', label: 'Add member', query: 'How to add a team member?' },
      { icon: '📞', label: 'Contact person', query: 'How to set the main contact person for guests?' },
    ],
  },
  '/dashboard/billing': {
    hr: [
      { icon: '💳', label: 'Pretplata', query: 'Koliko košta BookiApp i kako radi pretplata?' },
      { icon: '📊', label: 'Moj plan', query: 'Koji plan koristim i koliko jedinica imam?' },
    ],
    en: [
      { icon: '💳', label: 'Subscription', query: 'How much does BookiApp cost and how does subscription work?' },
      { icon: '📊', label: 'My plan', query: 'Which plan am I on and how many units do I have?' },
    ],
  },
  '/dashboard/settings': {
    hr: [
      { icon: '🌐', label: 'Jezik', query: 'Kako promijeniti jezik aplikacije?' },
      { icon: '👤', label: 'Profil', query: 'Kako urediti podatke profila?' },
    ],
    en: [
      { icon: '🌐', label: 'Language', query: 'How to change the app language?' },
      { icon: '👤', label: 'Profile', query: 'How to edit profile info?' },
    ],
  },
  '/dashboard/community': {
    hr: [
      { icon: '📝', label: 'Nova tema', query: 'Kako kreirati novu temu na forumu?' },
      { icon: '🤖', label: 'AI odgovori', query: 'Kako radi AI automatski odgovor na teme?' },
      { icon: '📌', label: 'Kategorije', query: 'Koje kategorije tema postoje na zajednici?' },
      { icon: '💡', label: 'Savjeti', query: 'Kako najbolje iskoristiti zajednicu iznajmljivača?' },
    ],
    en: [
      { icon: '📝', label: 'New thread', query: 'How to create a new thread on the forum?' },
      { icon: '🤖', label: 'AI answers', query: 'How does the AI auto-answer work on threads?' },
      { icon: '📌', label: 'Categories', query: 'What thread categories are available in the community?' },
      { icon: '💡', label: 'Tips', query: 'How to best use the landlord community?' },
    ],
  },
  '/dashboard/host-updates': {
    hr: [
      { icon: '📸', label: 'Nova objava', query: 'Kako objaviti novu objavu za goste?' },
      { icon: '⏰', label: 'Trajanje', query: 'Koliko dugo su objave vidljive gostima?' },
      { icon: '👥', label: 'Vidljivost', query: 'Kako odabrati koji gosti vide moju objavu?' },
      { icon: '💡', label: 'Ideje za objave', query: 'Što sve mogu objaviti za goste? Daj mi ideje!' },
    ],
    en: [
      { icon: '📸', label: 'New post', query: 'How to create a new post for guests?' },
      { icon: '⏰', label: 'Duration', query: 'How long are posts visible to guests?' },
      { icon: '👥', label: 'Visibility', query: 'How to choose which guests see my post?' },
      { icon: '💡', label: 'Post ideas', query: 'What can I post for guests? Give me ideas!' },
    ],
  },
  '/dashboard/properties': {
    hr: [
      { icon: '➕', label: 'Dodaj objekt', query: 'Kako dodati novi objekt?' },
      { icon: '🌐', label: 'Import s weba', query: 'Kako importirati podatke s moje web stranice?' },
      { icon: '⚙️', label: 'Default postavke', query: 'Kako postaviti zadane WiFi, parking i pravila za cijeli objekt?' },
      { icon: '📋', label: 'Tipovi objekata', query: 'Koja je razlika između kuće, vile i zgrade?' },
    ],
    en: [
      { icon: '➕', label: 'Add property', query: 'How to add a new property?' },
      { icon: '🌐', label: 'Web import', query: 'How to import data from my website?' },
      { icon: '⚙️', label: 'Default settings', query: 'How to set default WiFi, parking and rules for the whole property?' },
      { icon: '📋', label: 'Property types', query: 'What is the difference between house, villa and building?' },
    ],
  },
  '/dashboard/bookings': {
    hr: [
      { icon: '📸', label: 'Skeniraj potvrdu', query: 'Kako koristiti AI skener za unos rezervacije?' },
      { icon: '➕', label: 'Nova rezervacija', query: 'Kako kreirati novu rezervaciju?' },
      { icon: '👥', label: 'Grupna rezervacija', query: 'Kako napraviti grupnu rezervaciju za više apartmana?' },
      { icon: '🔍', label: 'Dostupnost', query: 'Koji apartmani su slobodni sljedeći tjedan?' },
    ],
    en: [
      { icon: '📸', label: 'Scan booking', query: 'How to use the AI scanner to enter a booking?' },
      { icon: '➕', label: 'New booking', query: 'How to create a new booking?' },
      { icon: '👥', label: 'Group booking', query: 'How to create a group booking for multiple units?' },
      { icon: '🔍', label: 'Availability', query: 'Which units are available next week?' },
    ],
  },
  '/dashboard/admin': {
    hr: [
      { icon: '👥', label: 'Korisnici', query: 'Kako upravljati korisnicima sustava?' },
      { icon: '👑', label: 'Beta testeri', query: 'Kako dodati i upravljati beta testerima?' },
      { icon: '📊', label: 'Statistike', query: 'Kakve statistike imam o sustavu?' },
    ],
    en: [
      { icon: '👥', label: 'Users', query: 'How to manage system users?' },
      { icon: '👑', label: 'Beta testers', query: 'How to add and manage beta testers?' },
      { icon: '📊', label: 'Statistics', query: 'What system statistics do I have?' },
    ],
  },
};

const DEFAULT_SUGGESTIONS = {
  hr: [
    { icon: '🏠', label: 'Dodaj objekt', query: 'Kako dodati novi objekt?' },
    { icon: '🛏️', label: 'Dodaj apartman', query: 'Kako dodati apartman?' },
    { icon: '📅', label: 'Nova rezervacija', query: 'Kako kreirati rezervaciju?' },
    { icon: '📊', label: 'Što nedostaje?', query: 'Što mi još nedostaje za setup?' },
    { icon: '📆', label: 'Calendar sync', query: 'Kako sinkronizirati kalendar s Booking.com?' },
    { icon: '💬', label: 'App za goste', query: 'Kako radi app za goste?' },
  ],
  en: [
    { icon: '🏠', label: 'Add property', query: 'How to add a new property?' },
    { icon: '🛏️', label: 'Add unit', query: 'How to add a unit?' },
    { icon: '📅', label: 'New booking', query: 'How to create a booking?' },
    { icon: '📊', label: "What's missing?", query: "What's still missing from my setup?" },
    { icon: '📆', label: 'Calendar sync', query: 'How to sync calendar with Booking.com?' },
    { icon: '💬', label: 'Guest App', query: 'How does the Guest App work?' },
  ],
};

// Support mode suggestions
const SUPPORT_SUGGESTIONS = {
  hr: [
    { icon: '🐛', label: 'Prijavi grešku', query: 'Imam problem - nešto ne radi kako treba' },
    { icon: '❓', label: 'Pitanje', query: 'Imam pitanje o korištenju aplikacije' },
    { icon: '💳', label: 'Naplata', query: 'Imam pitanje o pretplati ili naplati' },
    { icon: '💡', label: 'Prijedlog', query: 'Imam prijedlog za novu značajku' },
    { icon: '🔑', label: 'Pristup', query: 'Ne mogu pristupiti nečemu u aplikaciji' },
  ],
  en: [
    { icon: '🐛', label: 'Report bug', query: "I have a problem - something isn't working" },
    { icon: '❓', label: 'Question', query: 'I have a question about using the app' },
    { icon: '💳', label: 'Billing', query: 'I have a question about subscription or billing' },
    { icon: '💡', label: 'Suggestion', query: 'I have a suggestion for a new feature' },
    { icon: '🔑', label: 'Access', query: "I can't access something in the app" },
  ],
};

// Problem detection patterns - triggers support mode suggestion in assistant mode
const PROBLEM_PATTERNS = /ne radi|greška|error|bug|ne mogu|pogreška|problem|ne funkcionira|srušilo|zamrznulo|puklo|ne otvara|ne učitava|not working|broken|crashed|can't|won't load|won't open|doesn't work|issue|isn't working/i;

const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

export function useSmartAssistant(options?: SmartAssistantOptions & { initialMode?: AssistantMode }) {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  // Only 'hr' and 'en' suggestion sets exist. Any other i18n language (de, it,
  // a regional code, etc.) must fall back to 'en' so lookups never return
  // undefined → `.slice()` crash on the suggestion list (Sentry JAVASCRIPT-REACT-5).
  const langCode = i18n.language?.substring(0, 2);
  const language: 'hr' | 'en' = langCode === 'hr' ? 'hr' : 'en';

  const [messages, setMessages] = useState<SmartAssistantMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupCheckDone, setSetupCheckDone] = useState(false);
  // guidedMode now derives from the onboarding store. 'dismissed' = false,
  // anything else = true. This replaces the GUIDED_SETUP_KEY localStorage flag.
  const onboardingStatus = useOnboardingStore((s) => s.status);
  const dismissOnboarding = useOnboardingStore((s) => s.dismiss);
  const startGuidedOnboarding = useOnboardingStore((s) => s.startGuided);
  const guidedMode = onboardingStatus !== 'dismissed' && onboardingStatus !== 'completed';
  const [mode, setMode] = useState<AssistantMode>(options?.initialMode || 'assistant');
  const conversationRef = useRef<{ role: string; content: string }[]>([]);
  const supportExchangeRef = useRef(0);
  const createTicket = useCreateTicket();

  // Get quick suggestions based on mode, form context, or current page
  const quickSuggestions = useMemo(() => {
    // Support mode has its own suggestions
    if (mode === 'support') {
      return SUPPORT_SUGGESTIONS[language];
    }
    // 1. Form-specific suggestions (highest priority - user is editing a form)
    if (options?.formContext?.formType) {
      const formSugs = FORM_SUGGESTIONS[options.formContext.formType];
      if (formSugs?.[language]) return formSugs[language];
    }
    // 2. Page-specific suggestions (user is on a specific dashboard page)
    const path = location.pathname;
    for (const [pagePath, sugs] of Object.entries(PAGE_SUGGESTIONS)) {
      if (path.startsWith(pagePath)) {
        return sugs[language];
      }
    }
    // 3. Default suggestions
    return DEFAULT_SUGGESTIONS[language];
  }, [language, mode, options?.formContext?.formType, location.pathname]);

  // Time-of-day personalized welcome message
  const welcomeMessage = useMemo(() => {
    const name = user?.firstName || '';
    const greeting = getGreeting(name, language);

    if (mode === 'support') {
      if (language === 'hr') {
        return `${greeting}\n\n🛟 **Tehnička Podrška**\n\nOpiši mi problem koji imaš, a ja ću pokušati pomoći. Ako ne mogu riješiti problem, kreirat ću tiket za naš tim podrške.`;
      }
      return `${greeting}\n\n🛟 **Technical Support**\n\nDescribe the problem you're experiencing and I'll try to help. If I can't resolve it, I'll create a ticket for our support team.`;
    }

    if (language === 'hr') {
      return `${greeting}\n\nJa sam tvoj AI asistent - iskusan kolega koji poznaje BookiApp i vidi tvoje podatke. Pitaj me bilo što!`;
    }
    return `${greeting}\n\nI'm your AI assistant - an experienced fellow host who knows BookiApp and can see your data. Ask me anything!`;
  }, [language, user?.firstName, mode]);

  // Dismiss guided setup — delegate to the onboarding store.
  const handleDismissGuide = useCallback(() => {
    dismissOnboarding();
  }, [dismissOnboarding]);

  // Re-enable guided setup — delegate to the onboarding store.
  const handleRestartGuide = useCallback(() => {
    startGuidedOnboarding();
  }, [startGuidedOnboarding]);

  // Switch to support mode
  const switchToSupport = useCallback(() => {
    setMode('support');
    setMessages([]);
    conversationRef.current = [];
    supportExchangeRef.current = 0;
    setError(null);
    setSetupCheckDone(false);
  }, []);

  // Switch to assistant mode
  const switchToAssistant = useCallback(() => {
    setMode('assistant');
    setMessages([]);
    conversationRef.current = [];
    supportExchangeRef.current = 0;
    setError(null);
    setSetupCheckDone(false);
  }, []);

  // Create ticket from current conversation
  const createTicketFromConversation = useCallback(async (category: 'bug' | 'question' | 'billing' | 'feature_request' | 'account' | 'other' = 'bug') => {
    // Build conversation summary
    const conversationSummary = messages
      .filter(m => m.type === 'user' || (m.type === 'assistant' && !m.isSetupCheck))
      .map(m => `[${m.type === 'user' ? 'User' : 'AI'}]: ${m.content.slice(0, 200)}`)
      .join('\n');

    const browserInfo = captureBrowserContext();
    const subject = messages.find(m => m.type === 'user')?.content.slice(0, 100) || 'Support request';

    try {
      const ticket = await createTicket.mutateAsync({
        category,
        priority: 'medium',
        subject,
        description: messages.find(m => m.type === 'user')?.content || subject,
        ai_conversation_summary: conversationSummary || undefined,
        page_url: location.pathname,
        browser_info: browserInfo,
      });

      // Show confirmation message
      const confirmMsg: SmartAssistantMessage = {
        id: generateId(),
        type: 'assistant',
        content: `✅ **${t('support.ticketCreated', 'Ticket created!')}**\n\n${t('support.chatMode.ticketCreatedMessage', 'Your support request has been received. Our team will get back to you as soon as possible.\n\nYou can track ticket status on the [Support](/dashboard/support) page.')}`,
        timestamp: new Date(),
        actions: [{ type: 'navigate', target: '/dashboard/support', label: t('support.chatMode.openSupport', 'Open support') }],
      };
      setMessages(prev => [...prev, confirmMsg]);

      return ticket;
    } catch {
      const errorMsg: SmartAssistantMessage = {
        id: generateId(),
        type: 'assistant',
        content: `❌ ${t('support.chatMode.ticketErrorMessage', 'Error creating ticket. Please try again or contact us at admin@bookiapp.com.')}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
      return null;
    }
  }, [messages, createTicket, location.pathname, language]);

  // Send message to smart-landlord-assistant Edge Function
  const sendMessage = useCallback(async (question: string) => {
    if (!question.trim() || !user?.id) return;

    setError(null);
    setIsLoading(true);

    const isSetupCheck = question === '__setup_check__';

    // Add user message (unless it's a hidden setup check)
    if (!isSetupCheck) {
      const userMessage: SmartAssistantMessage = {
        id: generateId(),
        type: 'user',
        content: question.trim(),
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);

      // Add to conversation history for context
      conversationRef.current.push({ role: 'user', content: question.trim() });

      // Track support exchanges
      if (mode === 'support') {
        supportExchangeRef.current++;
      }
    }

    try {
      // Get Clerk JWT (HS256 signed via Supabase template) for Edge Function auth
      const clerkToken = getAuthToken();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/smart-landlord-assistant`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            ...(clerkToken ? { 'x-clerk-token': clerkToken } : {}),
          },
          body: JSON.stringify({
            question: question.trim(),
            language,
            currentPage: location.pathname,
            formContext: options?.formContext || null,
            conversationHistory: conversationRef.current.slice(-8),
            mode,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: SmartAssistantResponse = await response.json();

      if (data.rateLimited) {
        const rateLimitMsg: SmartAssistantMessage = {
          id: generateId(),
          type: 'assistant',
          content: data.answer,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, rateLimitMsg]);
        return;
      }

      // In assistant mode, detect technical problems and offer support switch
      let followups = data.suggested_followups || [];
      if (mode === 'assistant' && !isSetupCheck && PROBLEM_PATTERNS.test(question)) {
        const switchPrompt = `🛟 ${t('support.chatMode.switchToSupport', 'Switch to technical support')}`;
        if (!followups.includes(switchPrompt)) {
          followups = [switchPrompt, ...followups.slice(0, 2)];
        }
      }

      // In support mode, after 3+ exchanges, offer ticket creation
      let actions = data.actions || [];
      if (mode === 'support' && supportExchangeRef.current >= 3 && !actions.some(a => a.type === 'create_ticket')) {
        actions = [
          ...actions,
          {
            type: 'create_ticket' as const,
            target: 'bug',
            label: `📋 ${t('support.chatMode.createTicketLabel', 'Create support ticket')}`,
          },
        ];
      }

      const assistantMessage: SmartAssistantMessage = {
        id: generateId(),
        type: 'assistant',
        content: data.answer,
        timestamp: new Date(),
        actions,
        proactive_tips: data.proactive_tips,
        suggested_followups: followups,
        isSetupCheck: isSetupCheck,
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Add to conversation history
      if (!isSetupCheck) {
        conversationRef.current.push({ role: 'assistant', content: data.answer });
      }

      // Keep only last 8 messages in history
      if (conversationRef.current.length > 8) {
        conversationRef.current = conversationRef.current.slice(-8);
      }

    } catch (err) {
      console.error('Smart assistant error:', err);
      reportAIError(err, { feature: 'landlord_assistant', landlordId: user?.id });
      setError(t('support.chatMode.errorFetchingResponse', 'Error fetching response'));

      const errorMessage: SmartAssistantMessage = {
        id: generateId(),
        type: 'assistant',
        content: `😕 ${t('support.chatMode.oopsError', 'Oops, something went wrong. Please try again.')}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, language, location.pathname, options?.formContext, mode]);

  // Run setup check (once per session) - only in assistant mode, never in support mode
  const runSetupCheck = useCallback(async () => {
    if (setupCheckDone || !user?.id || mode === 'support') return;
    setSetupCheckDone(true);
    await sendMessage('__setup_check__');
  }, [setupCheckDone, user?.id, sendMessage, mode]);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    conversationRef.current = [];
    setError(null);
    setSetupCheckDone(false);
  }, []);

  // Execute action
  const executeAction = useCallback((action: AssistantAction) => {
    switch (action.type) {
      case 'navigate':
        if (options?.onNavigate) {
          options.onNavigate(action.target);
        } else {
          navigate(action.target);
        }
        break;
      case 'openModal':
        window.dispatchEvent(new CustomEvent('assistant:openModal', { detail: action.target }));
        break;
      case 'highlight': {
        // Resolve target as either:
        //   - "#cssId" / ".class" / "[data-onboarding='x']" — used verbatim
        //   - bare id like "property-type-selector" — auto-prefixed to
        //     "[data-onboarding='property-type-selector']" so the AI can stay
        //     readable in its tool calls.
        const raw = action.target;
        const selector = /^[#.\[]/.test(raw) ? raw : `[data-onboarding="${raw}"]`;
        // Scroll the element into view so the spotlight ring lands on-screen.
        const el = document.querySelector(selector);
        if (el && 'scrollIntoView' in el) {
          (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        const store = useOnboardingStore.getState();
        store.setHighlight(selector);
        // Auto-clear after a few seconds so the spotlight doesn't linger forever.
        setTimeout(() => {
          if (useOnboardingStore.getState().highlightTarget === selector) {
            useOnboardingStore.getState().setHighlight(null);
          }
        }, 5000);
        break;
      }
      case 'generate_content':
        window.dispatchEvent(new CustomEvent('assistant:generateContent', { detail: action.target }));
        break;
      case 'create_ticket':
        createTicketFromConversation(action.target as 'bug' | 'question' | 'billing' | 'feature_request' | 'account' | 'other');
        break;
      case 'switch_to_support':
        switchToSupport();
        break;
      default:
        console.warn('Unknown action type:', action.type);
    }
  }, [navigate, options?.onNavigate, createTicketFromConversation, switchToSupport]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    executeAction,
    quickSuggestions,
    welcomeMessage,
    runSetupCheck,
    setupCheckDone,
    guidedMode,
    dismissGuide: handleDismissGuide,
    restartGuide: handleRestartGuide,
    mode,
    switchToSupport,
    switchToAssistant,
    createTicketFromConversation,
  };
}

export default useSmartAssistant;
